import React, { FormEvent } from 'react';
import { useForm } from '@inertiajs/react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

interface User {
  id: number;
  name: string;
  email: string;
  username: string;
  role: string;
  otp_required_for_login: boolean;
}

interface ProfileProps {
  user: User;
  errors?: string[];
}

const Profile: React.FC<ProfileProps> = ({ user, errors = [], notice }) => {
  const isAdminOrManager = user.role === 'admin' || user.role === 'manager';

  const { data, setData, put, processing, errors: formErrors } = useForm({
    user: {
      name: user.name || '',
      username: user.username || '',
    }
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    put('/profile', {
      preserveScroll: true,
    });
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-card-foreground">Profile</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your profile information and preferences.
          </p>
        </div>

        {/* Error Display */}
        {errors.length > 0 && (
          <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
            <ul className="list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <Card className="rounded-xl border bg-card text-card-foreground shadow">
          <CardHeader>
            <CardTitle>Profile Information</CardTitle>
            <CardDescription>
              Update your personal information below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Field - Editable for all users */}
              <div className="flex flex-col space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  name="user[name]"
                  type="text"
                  value={data.user.name}
                  onChange={(e) => setData('user.name', e.target.value)}
                  placeholder="Enter your name"
                />
                {formErrors['user.name'] && (
                  <p className="text-sm text-destructive">{formErrors['user.name']}</p>
                )}
              </div>

              {/* Email Field - Read only */}
              <div className="flex flex-col space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={user.email}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed.
                </p>
              </div>

              {/* Username Field - Editable for all users */}
              <div className="flex flex-col space-y-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  name="user[username]"
                  type="text"
                  value={data.user.username}
                  onChange={(e) => setData('user.username', e.target.value)}
                  placeholder="Enter your username"
                />
                {formErrors['user.username'] && (
                  <p className="text-sm text-destructive">{formErrors['user.username']}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Username must be unique.
                </p>
              </div>

              {/* Role Field - Only visible for admins and managers */}
              {isAdminOrManager && (
                <div className="flex flex-col space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Input
                    id="role"
                    name="role"
                    type="text"
                    value={user.role}
                    disabled
                    className="bg-muted capitalize"
                  />
                </div>
              )}

              {/* Save Button */}
              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={processing}
                >
                  {processing ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Profile Settings - Only visible to admins and managers */}
        {isAdminOrManager && (
          <Card className="rounded-xl border bg-card text-card-foreground shadow">
            <CardHeader>
              <CardTitle>Profile Settings</CardTitle>
              <CardDescription>
                Additional profile management features will be available here.
              </CardDescription>
            </CardHeader>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Profile;
